import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, RefreshCw, Image as ImageIcon, Sparkles, Pencil, Check, X, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Default section info for fallbacks
const DEFAULT_SECTIONS = [
  { title: 'Key Highlights', purpose: 'What are the main takeaways?' },
  { title: 'Why This Matters', purpose: 'Why should the reader care?' },
  { title: 'What\'s Next', purpose: 'What action should they take?' },
  { title: 'The Details', purpose: 'Deeper dive into specifics' },
  { title: 'Looking Ahead', purpose: 'Future plans or closing thoughts' }
];

const MAX_SECTION_CHARS = 300;

function SortableSectionItem({ 
  id, 
  index, 
  imageUrl, 
  onAddImage, 
  onRemoveImage,
  sectionTitle,
  sectionPurpose,
  sectionText,
  onTitleChange,
  isEditingTitle,
  setEditingTitleIndex,
  onFetchSuggestions,
  onShortenText
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [tempTitle, setTempTitle] = useState(sectionTitle);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isShortening, setIsShortening] = useState(false);
  
  const charCount = sectionText?.length || 0;
  const charPercentage = Math.min((charCount / MAX_SECTION_CHARS) * 100, 100);
  const isOverLimit = charCount > MAX_SECTION_CHARS;

  const handleShortenClick = async () => {
    if (!onShortenText || !sectionText) return;
    setIsShortening(true);
    try {
      await onShortenText(index, sectionText);
    } finally {
      setIsShortening(false);
    }
  };

  const handleSaveTitle = () => {
    onTitleChange(index, tempTitle);
    setEditingTitleIndex(null);
  };

  const handleCancelEdit = () => {
    setTempTitle(sectionTitle);
    setEditingTitleIndex(null);
  };

  const handleSelectSuggestion = (suggestion) => {
    setTempTitle(suggestion);
    onTitleChange(index, suggestion);
    setShowSuggestions(false);
    setEditingTitleIndex(null);
  };

  const handleSparklesClick = async () => {
    if (showSuggestions) {
      setShowSuggestions(false);
      return;
    }
    
    // Show suggestions panel immediately
    setShowSuggestions(true);
    
    // If we have paragraph content and a fetch function, get AI suggestions
    if (sectionText && sectionText.length > 20 && onFetchSuggestions) {
      setLoadingSuggestions(true);
      try {
        const suggestions = await onFetchSuggestions(sectionText, index);
        if (suggestions && suggestions.length > 0) {
          setAiSuggestions(suggestions);
        }
      } catch (err) {
        console.error("Failed to fetch AI suggestions:", err);
      } finally {
        setLoadingSuggestions(false);
      }
    }
  };

  // Use AI suggestions if available, otherwise show a message
  const displaySuggestions = aiSuggestions.length > 0 ? aiSuggestions : [];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 bg-white rounded-xl border ${
        isDragging ? 'border-violet-400 shadow-lg' : 'border-slate-200'
      }`}
      data-testid={`section-item-${index}`}
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded mt-1"
          data-testid={`drag-handle-${index}`}
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>

        <div className="flex-1 min-w-0">
          {/* Section Title - Editable */}
          <div className="flex items-center gap-2 mb-2">
            {isEditingTitle ? (
              <div className="flex-1 flex items-center gap-2">
                <Input
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  className="h-8 text-sm font-semibold"
                  placeholder="Section title..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <Button variant="ghost" size="sm" onClick={handleSaveTitle} className="h-8 w-8 p-0 text-green-600">
                  <Check className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCancelEdit} className="h-8 w-8 p-0 text-slate-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <span className="text-sm font-semibold text-slate-800">{sectionTitle}</span>
                <button 
                  onClick={() => {
                    setTempTitle(sectionTitle);
                    setEditingTitleIndex(index);
                  }}
                  className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-violet-600"
                  title="Edit title"
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button 
                  onClick={handleSparklesClick}
                  className="p-1 hover:bg-violet-50 rounded text-violet-500 hover:text-violet-700"
                  title="Get AI title suggestions based on content"
                  data-testid={`ai-suggestions-btn-${index}`}
                >
                  <Sparkles className="w-3 h-3" />
                </button>
              </>
            )}
          </div>

          {/* AI Suggestions Dropdown */}
          {showSuggestions && !isEditingTitle && (
            <div className="mb-3 p-3 bg-violet-50 rounded-lg border border-violet-100">
              {loadingSuggestions ? (
                <div className="flex items-center gap-2 text-violet-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-xs font-medium">Analyzing content for suggestions...</span>
                </div>
              ) : displaySuggestions.length > 0 ? (
                <>
                  <p className="text-xs text-violet-600 font-medium mb-2">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    AI suggestions based on your content:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {displaySuggestions.map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className="px-2 py-1 text-xs bg-white text-violet-700 rounded-full border border-violet-200 hover:bg-violet-100 hover:border-violet-300 transition-colors"
                        data-testid={`suggestion-${index}-${i}`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-xs text-slate-500">
                  {sectionText && sectionText.length > 20 
                    ? "Click to generate AI suggestions based on paragraph content"
                    : "Add more content to this section to get AI-powered title suggestions"}
                </p>
              )}
            </div>
          )}

          {/* Purpose hint */}
          <p className="text-xs text-slate-500 mb-3 italic">
            💡 {sectionPurpose}
          </p>

          {/* Character count indicator */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-slate-400">Content length</span>
              <span className={`text-xs ${isOverLimit ? 'text-amber-600' : 'text-slate-400'}`}>
                {charCount} chars
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  isOverLimit ? 'bg-amber-400' : 
                  charCount > MAX_SECTION_CHARS * 0.8 ? 'bg-amber-300' : 'bg-emerald-400'
                }`}
                style={{ width: `${charPercentage}%` }}
              />
            </div>
            {/* Friendly message and AI Shorten button when content is long */}
            {isOverLimit && (
              <div className="mt-2 flex items-center justify-between gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-xs text-amber-700">
                  💡 Shorter sections get more engagement!
                </p>
                {onShortenText && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShortenClick}
                    disabled={isShortening}
                    className="text-amber-700 border-amber-200 hover:bg-amber-100 text-xs h-7 px-2"
                    data-testid={`shorten-btn-${index}`}
                  >
                    {isShortening ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Shortening...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" />
                        Shorten with AI
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Image section */}
          <div className="flex items-center gap-2">
            {imageUrl ? (
              <div className="flex items-center gap-3 flex-1">
                <div className="relative group">
                  <img 
                    src={imageUrl} 
                    alt={`Section ${index + 1}`}
                    className="w-20 h-14 object-cover rounded border border-slate-200"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="56"><rect fill="%23f1f5f9" width="80" height="56"/><text fill="%2394a3b8" x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="10">Error</text></svg>';
                    }}
                  />
                </div>
                <span className="text-xs text-emerald-600 font-medium">
                  ✓ Image added
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAddImage(index)}
                  className="text-slate-600 border-slate-200 hover:bg-slate-50 ml-auto"
                  data-testid={`replace-image-${index}`}
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Replace
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAddImage(index)}
                className="text-violet-600 border-violet-200 hover:bg-violet-50"
                data-testid={`add-image-btn-${index}`}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Image
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SectionImageManager({
  numSections = 3,
  sectionImages = {},
  sectionTexts = {},
  customTitles = {},
  onSectionImagesChange,
  onAddImageClick,
  onTitlesChange,
  onFetchSuggestions,
  onShortenText,
}) {
  const [editingTitleIndex, setEditingTitleIndex] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create array of section IDs
  const sectionIds = Array.from({ length: numSections }, (_, i) => `section-${i}`);

  const handleTitleChange = (index, newTitle) => {
    const newTitles = { ...customTitles, [index]: newTitle };
    if (onTitlesChange) {
      onTitlesChange(newTitles);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = sectionIds.indexOf(active.id);
      const newIndex = sectionIds.indexOf(over.id);

      // Reorder section images
      const newSectionImages = {};
      const oldKeys = Object.keys(sectionImages).map(Number).sort((a, b) => a - b);
      const reorderedKeys = arrayMove(oldKeys, oldIndex, newIndex);

      reorderedKeys.forEach((oldKey, newIdx) => {
        if (sectionImages[oldKey.toString()]) {
          newSectionImages[newIdx.toString()] = sectionImages[oldKey.toString()];
        }
      });

      onSectionImagesChange(newSectionImages);
      
      // Also reorder custom titles
      if (onTitlesChange) {
        const newTitles = {};
        const titleKeys = Object.keys(customTitles).map(Number).sort((a, b) => a - b);
        const reorderedTitleKeys = arrayMove(titleKeys, oldIndex, newIndex);
        reorderedTitleKeys.forEach((oldKey, newIdx) => {
          if (customTitles[oldKey]) {
            newTitles[newIdx] = customTitles[oldKey];
          }
        });
        onTitlesChange(newTitles);
      }
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = { ...sectionImages };
    delete newImages[index.toString()];
    onSectionImagesChange(newImages);
  };

  return (
    <div className="space-y-3" data-testid="section-image-manager">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
          {sectionIds.map((id, index) => {
            const defaultSection = DEFAULT_SECTIONS[index] || DEFAULT_SECTIONS[0];
            return (
              <SortableSectionItem
                key={id}
                id={id}
                index={index}
                imageUrl={sectionImages[index.toString()]}
                onAddImage={onAddImageClick}
                onRemoveImage={handleRemoveImage}
                sectionTitle={customTitles[index] || defaultSection.title}
                sectionPurpose={defaultSection.purpose}
                sectionText={sectionTexts[index]}
                onTitleChange={handleTitleChange}
                isEditingTitle={editingTitleIndex === index}
                setEditingTitleIndex={setEditingTitleIndex}
                onFetchSuggestions={onFetchSuggestions}
                onShortenText={onShortenText}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      
      <p className="text-xs text-slate-400 mt-2">
        <GripVertical className="w-3 h-3 inline mr-1" />
        Drag to reorder • Click <Pencil className="w-3 h-3 inline mx-1" /> to edit title • Click <Sparkles className="w-3 h-3 inline mx-1" /> for AI suggestions
      </p>
    </div>
  );
}

export default SectionImageManager;
